import React from "react";
import { ValueGetterParams } from "@ag-grid-community/core";
import { TFunction } from "i18next";

import BusinessRole from "fe-shared/src/types/domain/BusinessRole";

import AdjustableStatus from "../components/AdjustableStatus";
import {
  DateRenderer,
  StatusRenderer,
  ParticipantRenderer,
  TransactionNumberRenderer,
} from "../components/TransactionsDataGrid/renderers";
import { TransactionStatus, TransactionType } from "../types";

export const ADJUSTABLE_SUBMITTED_STATUS = "Submitted (Adjustable)";

export const getStatusFilterOptions = (t: TFunction) => {
  const statuses = Object.values(TransactionStatus).map((item) => ({
    value: item,
    label: t(`transactions.statuses.${item}`),
  }));

  return [
    {
      value: "",
      label: t("transactions.any"),
    },
    {
      value: ADJUSTABLE_SUBMITTED_STATUS,
      label: <AdjustableStatus />,
    },
    ...statuses,
  ];
};

const ProcessorTransactionType = [
  TransactionType.Delivery,
  TransactionType.Shipment,
  TransactionType.Receipt,
  TransactionType.Disposition,
  TransactionType.Adjustment,
];

const HaulerTransactionType = [
  TransactionType.Receipt,
  TransactionType.Pickup,
  TransactionType.Delivery,
  TransactionType.Disposition,
  TransactionType.Adjustment,
];

type ConfigMap = {
  [key in BusinessRole]?: TransactionType[];
};

const configMap: ConfigMap = {
  [BusinessRole.Processor]: ProcessorTransactionType,
  [BusinessRole.Hauler]: HaulerTransactionType,
};

export const getTransactionTypes = (businessRoleCode?: BusinessRole | null) => {
  const currentType = businessRoleCode && configMap[businessRoleCode];

  return Object.values(currentType || TransactionType);
};

export const getTypeFilterOptions = (
  t: TFunction,
  businessRoleCode: BusinessRole | null
) =>
  getTransactionTypes(businessRoleCode).map((item) => ({
    value: item,
    label: t(`transactions.type.${item}`),
  }));

export const getCommonColumnDefs = (
  t: TFunction,
  businessRoleCode: BusinessRole | null = null
) => [
  {
    headerName: t("transactions.grid.cols.completedDate"),
    field: "completedDate",
    width: 200,
    minWidth: 200,
    cellRenderer: "completedDateRenderer",
    filter: "dateFilter",
    floatingFilterComponent: "dateRangeFilter",
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    headerName: t("transactions.grid.cols.type"),
    field: "transactionType",
    width: 200,
    minWidth: 200,
    sortable: false,
    cellRenderer: "typeRenderer",
    filter: "selectFilter",
    floatingFilterComponent: "multiSelectFilter",
    floatingFilterComponentParams: {
      id: "type-multi-select-filter",
      width: 200,
      suppressFilterButton: true,
      options: getTypeFilterOptions(t, businessRoleCode),
    },
    valueGetter: (params: ValueGetterParams) => ({
      transactionType: params.data?.transactionType,
      linkedTransactions: params.data?.linkedTransactions,
      isPaperForm: params.data?.isPaperForm,
      isInvoiceable: params.data?.isInvoiceable,
    }),
  },
  {
    headerName: t("transactions.grid.cols.transactionNumber"),
    field: "transactionNumber",
    cellRenderer: "transactionNumberRenderer",
    width: 170,
    minWidth: 170,
    filter: "textFilter",
    filterParams: {
      startFilteringThreshold: 1,
    },
    floatingFilterComponent: "floatingTextFilter",
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    valueGetter: (params: ValueGetterParams) => ({
      transactionNumber: params.data?.transactionNumber,
      invoiceNumber: params.data?.invoiceNumber,
    }),
  },
  {
    headerName: t("transactions.grid.cols.participants"),
    field: "participants",
    minWidth: 225,
    flex: 1,
    cellRenderer: "participantsRenderer",
    filter: "textFilter",
    filterParams: {
      startFilteringThreshold: 1,
    },
    floatingFilterComponent: "floatingTextFilter",
    floatingFilterComponentParams: {
      placeholder: t("transactions.grid.placeholder.participants"),
      suppressFilterButton: true,
    },
  },
  {
    headerName: t("transactions.grid.cols.claimNumber"),
    colId: "claimNumber",
    field: "claimNumber",
    minWidth: 120,
    filter: "textFilter",
    floatingFilterComponent: "floatingTextFilter",
    floatingFilterComponentParams: {
      placeholder: t("transactions.grid.placeholder.claimNumber"),
      suppressFilterButton: true,
    },
  },
  {
    headerName: t("transactions.grid.cols.trailerNumber"),
    colId: "trailerNumber",
    field: "trailerNumber",
    minWidth: 120,
    filter: "textFilter",
    floatingFilterComponent: "floatingTextFilter",
    floatingFilterComponentParams: {
      placeholder: t("transactions.grid.placeholder.trailerNumber"),
      suppressFilterButton: true,
    },
  },
  {
    headerName: t("transactions.grid.cols.statusCode"),
    colId: "statusCode",
    width: 160,
    minWidth: 160,
    valueGetter: (params: ValueGetterParams) => ({
      statusCode: params.data?.statusCode,
      isAdjustable: params.data?.isAdjustable,
    }),
    cellRenderer: "statusRenderer",
    filter: "singleSelectFilter",
    floatingFilterComponent: "singleSelectComponent",
    floatingFilterComponentParams: {
      id: "status-single-select-filter",
      width: 200,
      suppressFilterButton: true,
      options: getStatusFilterOptions(t),
    },
  },
];

export const getCommonRenderers = () => ({
  completedDateRenderer: DateRenderer,
  transactionNumberRenderer: TransactionNumberRenderer,
  participantsRenderer: ParticipantRenderer,
  statusRenderer: StatusRenderer,
});
